// Predicates behind the super admin table filter dropdowns. They are written
// against what each column *draws* rather than the raw field, so a filter can
// never disagree with the rows it is narrowing.

export type PaymentMethodKey = 'card' | 'manual' | 'other' | 'none'

/**
 * Buckets a `Payment.method` string. The column is free text on the backend
 * ('card', 'Bank Transfer', 'Visa **** 4242'), so there is no choice list to
 * read - this is the one place that decides what counts as what, shared with
 * `formatPaymentMethod` so the label and the filter cannot drift apart.
 */
export const paymentMethodKey = (method?: string | null): PaymentMethodKey => {
  const normalized = (method ?? '').trim().toLowerCase()
  if (!normalized) return 'none'
  if (normalized.includes('card')) return 'card'
  if (normalized.includes('manual') || normalized.includes('bank')) {
    return 'manual'
  }
  return 'other'
}

/**
 * An organisation's status is its subscription's, which has eight backend
 * states, but the column collapses every one that is not `active` into
 * "Pending" - including a null subscription. The filter collapses them the
 * same way, so picking Pending returns exactly the rows that read Pending.
 */
export const matchesOrganizationStatus = (
  status: string | null | undefined,
  filter: string,
) => {
  if (!filter) return true
  const isActive = status === 'active'
  return filter === 'active' ? isActive : !isActive
}

/** Compared lowercased and trimmed: `Payment.status` defaults to 'PENDING',
 *  which is not one of its own lower-case choices. */
export const matchesPaymentStatus = (
  status: string | null | undefined,
  filter: string,
) => {
  if (!filter) return true
  return (status ?? '').trim().toLowerCase() === filter
}
